(() => {
  if (window.TMCart) return;
  const originalFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || '/';
  const cartURL = () => `${root()}cart`;
  const state = { busy: false, uncertain: false, confirmed: null };
  const selectors = 'cart-items, [name="checkout"], .additional-checkout-buttons, .shopify-payment-button, [data-tm-submit], [data-mobile-action], [data-review-cart], .tm-cart, form[is="product-form"] [type="submit"], [type="submit"][form^="product-form"]';
  const held = new Map();
  function lockUI() {
    document.querySelectorAll(selectors).forEach(node => {
      if (!held.has(node)) held.set(node, { inert: node.hasAttribute('inert'), disabled: node.disabled, busy: node.getAttribute('aria-busy') });
      node.setAttribute('inert', '');
      node.setAttribute('aria-busy', 'true');
      if ('disabled' in node) node.disabled = true;
    });
    document.documentElement.dataset.tmCartBusy = 'true';
  }
  function finish() {
    if (!state.confirmed || state.uncertain) return;
    state.busy = false;
    held.forEach((old, node) => {
      node.toggleAttribute('inert', old.inert);
      if ('disabled' in node) node.disabled = old.disabled;
      old.busy === null ? node.removeAttribute('aria-busy') : node.setAttribute('aria-busy', old.busy);
    });
    held.clear();
    delete document.documentElement.dataset.tmCartBusy;
    document.dispatchEvent(new CustomEvent('tm:cart-ready', { detail: state.confirmed }));
  }
  function notice(message) {
    let box = document.getElementById('tm-cart-safety-notice');
    if (!box) {
      box = document.createElement('div');
      box.id = 'tm-cart-safety-notice'; box.setAttribute('role', 'alert');
      box.style.cssText = 'position:fixed;inset:auto 12px 12px;z-index:99999;background:#fff7e6;color:#352817;padding:18px;border:1px solid #9b792f;box-shadow:0 4px 24px #0003;font:16px/1.5 sans-serif';
      document.body.append(box);
    }
    box.replaceChildren(document.createTextNode(`${message} `));
    const link = document.createElement('a');
    link.href = cartURL(); link.textContent = 'Reload and review your cart'; link.style.textDecoration = 'underline';
    box.append(link);
  }
  function fail(message) {
    state.uncertain = true; lockUI(); notice(message);
  }
  async function read() {
    const response = await originalFetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw new Error('Cart could not be confirmed.');
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || !Number.isInteger(cart.total_price)) throw new Error('Cart response is incomplete.');
    return cart;
  }
  const identity = item => JSON.stringify([String(item.variant_id || item.id), item.properties || {}, item.selling_plan_allocation?.selling_plan?.id || null]);
  function totals(cart) {
    const result = new Map();
    cart.items.forEach(item => result.set(identity(item), (result.get(identity(item)) || 0) + item.quantity));
    return result;
  }
  function payload(body) {
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (_) { return Object.fromEntries(new URLSearchParams(body)); }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) return Object.fromEntries(body.entries());
    throw new Error('Please reload the cart before updating it.');
  }
  function expected(before, path, data) {
    const result = totals(before);
    const valid = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value));
    if (/\/add(?:\.js)?$/.test(path)) {
      const items = data.items || [data];
      items.forEach(item => {
        const qty = item.quantity ?? 1;
        if (!item.id || !valid(qty) || Number(qty) < 1) throw new Error('Enter a whole item quantity before adding.');
        const key = identity({ variant_id: item.id, properties: item.properties || {}, selling_plan_allocation: item.selling_plan ? { selling_plan: { id: item.selling_plan } } : null });
        result.set(key, (result.get(key) || 0) + Number(qty));
      });
    } else if (/\/change(?:\.js)?$/.test(path)) {
      if (!valid(data.quantity) || !data.id || data.line) throw new Error('Reload your cart before changing this item.');
      const item = before.items.find(item => item.key === String(data.id));
      if (!item) throw new Error('This item has changed. Reload your cart.');
      const key = identity(item);
      result.set(key, result.get(key) - item.quantity + Number(data.quantity));
    } else if (/\/update(?:\.js)?$/.test(path) && data.updates) {
      if (Array.isArray(data.updates)) throw new Error('Reload your cart before updating quantities.');
      Object.entries(data.updates).forEach(([key, qty]) => {
        const item = before.items.find(item => item.key === key);
        if (!item || !valid(qty)) throw new Error('This item has changed. Reload your cart.');
        const group = identity(item);
        result.set(group, result.get(group) - item.quantity + Number(qty));
      });
    } else if (/\/clear(?:\.js)?$/.test(path)) result.clear();
    return result;
  }
  function matches(wanted, actual) {
    return [...new Set([...wanted.keys(), ...actual.keys()])].every(key => (wanted.get(key) || 0) === (actual.get(key) || 0));
  }
  window.TMCart = { state, read, finish, matches, totals };
  window.fetch = async function(input, options = {}) {
    const url = new URL(typeof input === 'string' ? input : input.url, location.href);
    const method = String(options.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(?:\.js)?$/.test(url.pathname)) return originalFetch(input, options);
    if (state.busy || state.uncertain) throw new Error('A cart update is already being checked. Review your cart before trying again.');
    state.busy = true; state.confirmed = null; lockUI();
    let attempted = false;
    try {
      const before = await read();
      const wanted = expected(before, url.pathname, payload(options.body));
      attempted = true;
      const response = await originalFetch(input, { ...options, signal: options.signal || AbortSignal.timeout(25000) });
      const changed = await response.clone().json();
      if (!response.ok || changed.status || changed.errors) throw new Error(changed.description || 'The cart update could not be confirmed.');
      const cart = await read();
      if (!matches(wanted, totals(cart))) throw new Error('Shopify returned a different quantity. Review your cart before continuing.');
      state.confirmed = cart;
      document.dispatchEvent(new CustomEvent('tm:cart-confirmed', { detail: cart }));
      setTimeout(finish, 1200);
      return response;
    } catch (error) {
      if (attempted) fail(`${error.message} Do not add again until you have checked the cart.`);
      else { state.confirmed = {}; finish(); notice(error.message); }
      throw error;
    }
  };
  document.addEventListener('cart:updated', finish);
  function confirmEditedQuantities(event) {
    const dirty = [...document.querySelectorAll('cart-items input[name="updates[]"]')].filter(input => input.value !== input.defaultValue);
    if (!dirty.length) return false;
    event.preventDefault(); event.stopImmediatePropagation();
    const input = dirty[0];
    if (!/^\d+$/.test(input.value.trim())) {
      input.value = input.defaultValue;
      notice('The blank or invalid quantity was not applied. Review your cart before checkout.');
    } else input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  document.addEventListener('submit', event => {
    if (!state.busy && event.target.matches('form[action$="/cart"]') && confirmEditedQuantities(event)) return;
    if (state.busy && event.target.matches('form[action*="/cart"], form[is="product-form"]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('click', event => {
    if (!state.busy && event.target.closest('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons, .shopify-payment-button') && confirmEditedQuantities(event)) return;
    if (!state.busy) return;
    if (event.target.closest('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons, .shopify-payment-button, cart-items, [data-tm-submit], [data-mobile-action], [data-review-cart], .tm-cart')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  new MutationObserver(() => { if (state.busy) lockUI(); }).observe(document.documentElement, { childList: true, subtree: true });
})();
